import React, { useEffect, useState } from 'react';
import useLicenseStore from '../store/licenseStore';
import HSTLogo from '../components/HSTLogo';
import { useTranslation } from '../i18n';
import { Hourglass, KeyRound, Loader2 } from 'lucide-react';

function pad(n) {
  return String(n).padStart(2, '0');
}

export default function WaitingRoom() {
  const { t } = useTranslation();
  const { waitingInfo, validateLicense, clearLicense } = useLicenseStore();
  const [now, setNow] = useState(Date.now());
  const [entering, setEntering] = useState(false);

  const startsAt = waitingInfo?.startsAt;
  const target = startsAt ? new Date(startsAt).getTime() : 0;
  const diff = Math.max(0, target - now);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Countdown hit zero → auto re-validate (server is the source of truth).
  useEffect(() => {
    if (diff === 0 && waitingInfo?.licenseKey && !entering) {
      setEntering(true);
      validateLicense(waitingInfo.licenseKey).then((r) => {
        if (!r.valid) setTimeout(() => setEntering(false), 15000);
      });
    }
  }, [diff, entering, waitingInfo, validateLicense]);

  // Periodic server re-check (the admin may move the start date).
  useEffect(() => {
    if (!waitingInfo?.licenseKey) return undefined;
    const id = setInterval(() => {
      if (new Date(waitingInfo.startsAt).getTime() - Date.now() > 2000) {
        validateLicense(waitingInfo.licenseKey);
      }
    }, 60000);
    return () => clearInterval(id);
  }, [waitingInfo, validateLicense]);

  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);

  const blocks = [
    { v: d, label: t('waiting_days'), tid: 'countdown-days' },
    { v: h, label: t('waiting_hours'), tid: 'countdown-hours' },
    { v: m, label: t('waiting_minutes'), tid: 'countdown-minutes' },
    { v: s, label: t('waiting_seconds'), tid: 'countdown-seconds' },
  ];

  const opensLabel = startsAt
    ? new Date(startsAt).toLocaleString('fr-FR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '';

  return (
    <div className="min-h-screen bg-ink-900 flex items-center justify-center p-4 relative overflow-hidden" data-testid="waiting-room">
      <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/85 to-background" />
      <div className="absolute inset-0 bg-grid-dots opacity-30" />

      <div className="relative z-10 w-full max-w-lg text-center">
        <div className="flex flex-col items-center gap-3 mb-8">
          <HSTLogo size={72} />
          <h1 className="font-display text-4xl font-black tracking-tight text-foreground">HST</h1>
        </div>

        <div className="surface-card bg-card/95 backdrop-blur-xl border-ink-600 p-8">
          <div className="mx-auto w-14 h-14 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center mb-4">
            {diff === 0 || entering
              ? <Loader2 className="w-7 h-7 text-accent animate-spin" />
              : <Hourglass className="w-7 h-7 text-accent animate-pulse" />}
          </div>

          <h2 className="font-display text-xl font-bold text-foreground mb-1" data-testid="waiting-room-title">
            {t('waiting_room_title')}
          </h2>
          {waitingInfo?.holderName && (
            <p className="text-sm text-slate-400 mb-4" data-testid="waiting-holder-name">{waitingInfo.holderName}</p>
          )}

          {diff === 0 || entering ? (
            <p className="text-accent font-semibold py-6" data-testid="waiting-entering">{t('waiting_room_entering')}</p>
          ) : (
            <>
              <p className="text-sm text-slate-300">
                {t('waiting_room_opens')}{' '}
                <span className="font-semibold text-foreground" data-testid="waiting-opens-at">{opensLabel}</span>
              </p>

              <div className="grid grid-cols-4 gap-3 my-6" data-testid="waiting-countdown">
                {blocks.map((b) => (
                  <div key={b.tid} className="bg-background border border-ink-600 rounded-lg py-3">
                    <div className="text-2xl font-black font-mono text-accent" data-testid={b.tid}>{pad(b.v)}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{b.label}</div>
                  </div>
                ))}
              </div>

              <p className="text-[11px] text-slate-500">{t('waiting_room_auto')}</p>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={clearLicense}
          className="mt-5 inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-300 uppercase tracking-wider transition-colors"
          data-testid="waiting-change-key"
        >
          <KeyRound className="w-3.5 h-3.5" /> {t('waiting_change_key')}
        </button>
      </div>
    </div>
  );
}
